import { useEffect } from 'react';
import { App, Form, Input, Modal } from 'antd';
import { create } from '@bufbuild/protobuf';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { channelApi } from '@/api/channel.api';
import { ApiError } from '@/api/errors';
import { type ChannelAccount } from '@/proto/agentplatform/v1/channel_pb';
import { UpdateChannelAccountRequestSchema } from '@/proto/agentplatform/httpv2/channel_pb';

interface Props {
  open: boolean;
  account: ChannelAccount;
  onClose: () => void;
}

interface FormValues {
  name: string;
}

/** 编辑渠道账号弹窗（对齐参考页 Modal）。真实可保存字段仅 name。 */
export default function EditWsAccountModal({ open, account, onClose }: Props) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ name: account.name || '' });
    }
  }, [open, account, form]);

  const updateMutation = useMutation({
    mutationFn: async (name: string) => {
      // 仅提交 name；v2 API 其它字段留空即保留原值，credential 也无需回传。
      await channelApi.updateChannelAccount(
        create(UpdateChannelAccountRequestSchema, {
          id: account.id,
          name,
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel_account', account.id] });
      queryClient.invalidateQueries({ queryKey: ['channel_accounts'] });
      message.success('渠道账号已更新');
      onClose();
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : (e as Error).message),
  });

  async function onOk() {
    const v = await form.validateFields();
    updateMutation.mutate(v.name.trim());
  }

  return (
    <Modal
      title="编辑渠道账号"
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText="保存"
      cancelText="取消"
      confirmLoading={updateMutation.isPending}
      width={520}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="账号名称"
          name="name"
          rules={[{ required: true, message: '账号名称必填' }]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
}
